import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import moment from "moment";
import React from "react";

interface Permission {
  permissionAttributeName: string;
  accessStatus: boolean;
}

export default function getTeamMember(
  teamMemberId: string,
  teamsData: any
): any {
  const teamMember = teamsData?.find((team: any) => team?.id === teamMemberId);
  return teamMember || null;
}

export function getCourse(courseId: string, courseData: any): any {
  const course = courseData?.find((item: any) => item?.id === courseId);
  return course || null;
}

export function getDescriptionWithMentionedNames(
  description: any,
  employees: any
): any {
  const employeeNameMap: Record<string, string> = {};

  employees?.forEach((employee: any) => {
    const formattedFirstName =
      employee?.firstName?.charAt(0)?.toUpperCase() +
      employee?.firstName?.slice(1);
    const formattedLastName =
      employee?.lastName?.charAt(0)?.toUpperCase() +
      employee?.lastName?.slice(1);
    employeeNameMap[employee.id] = `${formattedFirstName} ${formattedLastName}`;
  });

  const replacedDescription = description?.replace(
    /@{{([a-zA-Z0-9-]+)}}/g,
    (match: any, employeeId: any) => {
      const employeeName = employeeNameMap[employeeId];
      return employeeName
        ? `<span style='background-color:#F5F5F7;color: #667085;border-radius: 10px;padding: 0 10px;'>@${employeeName}</span>`
        : match;
    }
  );

  return replacedDescription || "";
}

export function getCombinedAssignees(assignees: any[], branchId: string) {
  const branchEmployees =
    assignees?.filter((item: any) => item?.branchId === branchId) || [];

  const otherBranchEmployees =
    assignees?.filter(
      (item: any) =>
        item?.branchId !== branchId && item?.allBranchAccess === true
    ) || [];

  const combinedEmployees = [...branchEmployees, ...otherBranchEmployees];
  const filteredCombinedEmployees =
    combinedEmployees?.filter(
      (item: any) => item?.status === "ACTIVE" && item?.leadCardAccess === true
    ) || [];

  return filteredCombinedEmployees;
}

export function checkObjectsEqual(obj1: any, obj2: any) {
  const str1 = JSON.stringify(obj1);
  const str2 = JSON.stringify(obj2);

  return str1 === str2;
}

export function formatAmount(revenue: number): string {
  if (revenue >= 1_00_00_000) {
    return (
      (revenue / 1_00_00_000)?.toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      }) + " Crore"
    );
  } else if (revenue >= 1_00_000) {
    return (
      (revenue / 1_00_000)?.toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      }) + " Lakh"
    );
  } else {
    return revenue?.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });
  }
}

export function RevenueDisplay(revenue: number) {
  const formattedAmount = formatAmount(revenue) || "";
  if (
    formattedAmount.toString().includes("Lakh") ||
    formattedAmount.toString().includes("Crore")
  ) {
    return revenue?.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });
  } else {
    return;
  }
}

export function addSpaceBeforeCaps(str: any) {
  return str.replace(/([A-Z])/g, " $1").trim();
}

export const getMonths = (format = "MMMM") => {
  return moment.months().map((month, index) => {
    return { id: index + 1, value: moment(month, "MMMM").format(format) };
  });
};

export const getYears = (startYear: number, endYear: number) => {
  let years = [];
  let i = 0;
  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }
  return years;
};
export function filterObjectKeys(data: any, reference: any) {
  const filteredData: any = {};
  Object.keys(reference).forEach((key) => {
    if (data?.hasOwnProperty(key)) {
      filteredData[key] = data[key];
    }
  });
  return filteredData;
}

export function findCountryByName(name: string, countries: any[]) {
  return (
    countries?.find(
      (country: any) => country?.name?.toLowerCase() === name?.toLowerCase()
    ) || null
  );
}

export function findBranchById(branchId: string, branchData: any): any {
  const branch = branchData?.find((item: any) => item?.id === branchId);
  return branch || null;
}
export function getTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
}

export function base64ToBlob(base64: string, mimeType: string): Promise<Blob> {
  const dataUrl = `data:${mimeType};base64,${base64}`;
  return fetch(dataUrl).then((res) => res.blob());
}

export const base64ToPdfAndOpen = async (base64: string) => {
  try {
    const cleanedBase64 = base64.replace(/^data:application\/pdf;base64,/, "");

    const blob = await fetch(
      `data:application/pdf;base64,${cleanedBase64}`
    ).then((res) => res.blob());
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  } catch (err) {
    console.error("Failed to convert base64 to PDF:", err);
  }
};

export const getCountryCodeFromCallingCode = (
  callingCode: string
): string | null => {
  if (!callingCode) return null;

  const countryCode = getCountries().find(
    (code) => getCountryCallingCode(code) === callingCode
  );

  return countryCode || null;
};

export function getCombinedAssigneesForBulkMove(
  assignees: any[],
  branchId: string
) {
  const branchEmployees =
    assignees?.filter((item: any) => item?.branchId === branchId) || [];

  const combinedEmployees = [...branchEmployees];
  const filteredCombinedEmployees =
    combinedEmployees?.filter(
      (item: any) => item?.status === "ACTIVE" && item?.leadCardAccess === true
    ) || [];

  return filteredCombinedEmployees;
}

// export function highlightMatch(text: string, search: string) {
//   if (!search) return text;

//   const regex = new RegExp(`(${search})`, "gi");
//   const parts = text.split(regex);

//   return parts.map((part, index) =>
//     part.toLowerCase() === search.toLowerCase() ? (
//       <mark key={index} style={{ backgroundColor: "#11C89B29" }}>
//         {part}
//       </mark>
//     ) : (
//       part
//     )
//   );
// }

export function highlightMatch(text: string, search: string) {
  if (!search) return text;

  // Split text into words
  const words = text.split(/\s+/);
  const result: React.ReactNode[] = [];

  words.forEach((word, wordIndex) => {
    if (
      word.toLowerCase().startsWith(search.toLowerCase()) &&
      search.length <= word.length
    ) {
      const match = word.slice(0, search.length);
      const rest = word.slice(search.length);

      result.push(
        <React.Fragment key={wordIndex}>
          <mark style={{ backgroundColor: "#11C89B29" }}>{match}</mark>
          {rest}
        </React.Fragment>
      );
    } else {
      result.push(<React.Fragment key={wordIndex}>{word}</React.Fragment>);
    }

    // Add space between words except after last
    if (wordIndex < words.length - 1) {
      result.push(" ");
    }
  });

  return result;
}

export const mergeFilters = (filterGroups: any[][]) => {
  const mergedMap: Record<string, Set<string>> = {};
  filterGroups?.flat()?.forEach(({ key, value }) => {
    if (!mergedMap[key]) {
      mergedMap[key] = new Set(value);
    } else {
      value.forEach((v: string) => mergedMap[key].add(v));
    }
  });

  return Object.entries(mergedMap).map(([key, valSet]) => ({
    key,
    value: Array.from(valSet),
  }));
};

export function formatTaskCategory(category: string): string {
  if (category === "Payment Overdue Task") {
    return "OVERDUE_PAYMENT";
  }
  return category.toUpperCase().replace(/ /g, "_");
}
